import { Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { Tooltip } from "components/tooltip";
import {
  memo,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// ─── Types ───────────────────────────────────────────────────────────

export interface SegmentedIconSwitchOption {
  value: string;
  label: string;
  icon: ReactNode;
  width?: number;
  activeColor?: string;
  activeIconColor?: string;
  activeTextColor?: string;
  inactiveIconColor?: string;
  inactiveTextColor?: string;
  disabled?: boolean;
  tooltip?: string;
  /** Per-option override for the inactive option row (wins over `optionSx`). */
  sx?: SxProps<Theme>;
  /** Per-option override for the active pill when this option is selected (wins over `activeSx`). */
  activeSx?: SxProps<Theme>;
  /** Per-option override for the label typography (wins over `labelSx`). */
  labelSx?: SxProps<Theme>;
}

export interface SegmentedIconSwitchProps {
  options: SegmentedIconSwitchOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  size?: "small" | "medium";
  /** Override for the outer track container. */
  containerSx?: SxProps<Theme>;
  /** Override applied to every inactive option row (supports `&:hover` etc.). */
  optionSx?: SxProps<Theme>;
  /** Override for the active (selected) pill. */
  activeSx?: SxProps<Theme>;
  /** Override applied to every label typography. */
  labelSx?: SxProps<Theme>;
  "aria-label"?: string;
  showLabels?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────

export const SegmentedIconSwitch = memo(
  ({
    options,
    value,
    onChange,
    disabled = false,
    size = "medium",
    containerSx,
    optionSx,
    activeSx,
    labelSx,
    "aria-label": ariaLabel,
    showLabels = false,
  }: SegmentedIconSwitchProps) => {
    const theme = useTheme();
    const refs = useRef<Array<HTMLDivElement | HTMLSpanElement>>([]);

    const containerHeight = size === "small" ? "28px" : "32px";
    const iconSize = size === "small" ? 16 : 20;

    const [widths, setWidths] = useState<Array<number | undefined>>([]);

    const getSelectedPosition = () => {
      const selectedIndex = options.findIndex(
        (option) => option.value === value,
      );
      let position = 0;
      for (let i = 0; i < selectedIndex; i++) {
        position += widths[i] || options[i]?.width || 0;
      }
      return options[selectedIndex]?.width ? position + 1 : position;
    };

    const selectedItem = useMemo(() => {
      return options.find((opt) => opt.value === value);
    }, [value]);

    useLayoutEffect(() => {
      const list = refs.current.map((ref) => ref?.offsetWidth);
      setWidths(list);
    }, [value]);

    return (
      <Stack
        direction={"row"}
        sx={{
          display: "flex",
          position: "relative",
          padding: "2px",
          height: containerHeight,
          borderRadius: "4px",
          width: "fit-content",
          alignItems: "center",
          bgcolor: theme.palette.neutral["005"],
          "&:hover": {
            cursor: disabled ? "not-allowed" : "pointer",
          },
          overflow: "hidden",
          ...containerSx,
        }}
        onClick={(e) => {
          if (disabled || options.length > 2) return;
          e.stopPropagation();
          const currentIndex = options.findIndex((opt) => opt.value === value);
          const nextIndex = (currentIndex + 1) % options.length;
          const nextOption = options[nextIndex];
          if (nextOption.disabled) return;
          onChange(nextOption.value);
        }}
      >
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isOptionDisabled = option.disabled && !isSelected;
          const showLabel = isSelected || showLabels;
          const optionPadding = showLabel ? "6px 8px" : "6px 8px";

          const optionNode = (
            <Stack
              ref={(el) => {
                if (el) {
                  refs.current[index] = el;
                }
              }}
              key={option.value}
            >
              <Stack
                key={option.value}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"4px"}
                sx={[
                  {
                    visibility: isSelected ? "hidden" : "visible",
                    color: theme.palette.lightBg.low,
                    textAlign: "center",
                    width: isSelected ? (option.width ?? "auto") : "auto",
                    padding: optionPadding,
                    whiteSpace: "nowrap",
                    ...(isOptionDisabled && { opacity: 0.4 }),
                    "& svg": {
                      width: iconSize,
                      height: iconSize,
                      color: option.inactiveIconColor,
                    },
                  },
                  ...(Array.isArray(optionSx) ? optionSx : [optionSx]),
                  ...(Array.isArray(option.sx) ? option.sx : [option.sx]),
                ]}
                onClick={(e) => {
                  if (disabled || isOptionDisabled || options.length <= 2)
                    return;
                  e.stopPropagation();

                  const nextIndex = index % options.length;
                  onChange(options[nextIndex].value);
                }}
              >
                {option.icon && option.icon}
                {showLabel && (
                  <Typography
                    key={option.value}
                    variant="body-small"
                    sx={[
                      ...(Array.isArray(labelSx) ? labelSx : [labelSx]),
                      ...(Array.isArray(option.labelSx)
                        ? option.labelSx
                        : [option.labelSx]),
                      {
                        color:
                          option.inactiveTextColor ??
                          theme.palette.lightBg.low,
                      },
                    ]}
                    ref={(el) => {
                      if (el) {
                        refs.current[index] = el;
                      }
                    }}
                  >
                    {option.label}
                  </Typography>
                )}
              </Stack>
            </Stack>
          );

          if (isOptionDisabled && option.tooltip) {
            return (
              <Tooltip
                key={option.value}
                title={option.tooltip}
                disableSentenceCase
              >
                {optionNode}
              </Tooltip>
            );
          }

          return optionNode;
        })}
        <Stack
          sx={[
            {
              flexDirection: "row",
              alignItems: "center",
              gap: "4px",
              padding: "6px 8px",
              position: "absolute",
              bgcolor: selectedItem?.activeColor ?? theme.palette.neutral.main,
              boxShadow: "2px 2px 6px 0px #0000001F",
              textAlign: "center",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              width: selectedItem?.width ? selectedItem.width - 2 : "auto",
              height: "24px",
              transform: `translateX(${getSelectedPosition()}px)`,
              transition: "transform 0.2s ease, width 0.2s ease",
              "& svg": {
                width: iconSize,
                height: iconSize,
                color: selectedItem?.activeIconColor,
              },
            },
            ...(Array.isArray(activeSx) ? activeSx : [activeSx]),
            ...(Array.isArray(selectedItem?.activeSx)
              ? selectedItem.activeSx
              : [selectedItem?.activeSx]),
          ]}
        >
          {selectedItem?.icon}{" "}
          <Typography
            variant="body-small"
            sx={[
              ...(Array.isArray(labelSx) ? labelSx : [labelSx]),
              ...(Array.isArray(selectedItem?.labelSx)
                ? selectedItem.labelSx
                : [selectedItem?.labelSx]),
              { color: selectedItem?.activeTextColor },
            ]}
          >
            {selectedItem?.label}
          </Typography>
        </Stack>
      </Stack>
    );
  },
);
